import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Calculator,
  ChevronRight,
  Clock3,
  Command,
  FileStack,
  Heart,
  Home,
  Image as ImageIcon,
  Menu,
  Pin,
  Search,
  Settings,
  Type,
  X,
} from 'lucide-react'
import { BrandMark } from './components/brand-mark'
import { categories, findTool, searchTools, tools, type CategoryFilter, type ToolDefinition } from './registry/tools'
import { useDrawerStore } from './state/drawer-store'
import { localGreeting } from './utils/local-greeting'

const SipWorkspace = lazy(async () => ({ default: (await import('./tools/finance-workspaces')).SipWorkspace }))
const CagrWorkspace = lazy(async () => ({ default: (await import('./tools/finance-workspaces')).CagrWorkspace }))
const EmiWorkspace = lazy(async () => ({ default: (await import('./tools/finance-workspaces')).EmiWorkspace }))
const JsonWorkspace = lazy(async () => ({ default: (await import('./tools/json/workspace')).JsonWorkspace }))
const ImageCompressWorkspace = lazy(async () => ({ default: (await import('./tools/image/workspaces')).ImageCompressWorkspace }))
const ImageResizeWorkspace = lazy(async () => ({ default: (await import('./tools/image/workspaces')).ImageResizeWorkspace }))
const ImageConvertWorkspace = lazy(async () => ({ default: (await import('./tools/image/workspaces')).ImageConvertWorkspace }))
const ImageMetadataWorkspace = lazy(async () => ({ default: (await import('./tools/image/workspaces')).ImageMetadataWorkspace }))
const PdfMergeWorkspace = lazy(async () => ({ default: (await import('./tools/pdf/workspaces')).PdfMergeWorkspace }))
const PdfSplitWorkspace = lazy(async () => ({ default: (await import('./tools/pdf/workspaces')).PdfSplitWorkspace }))
const ImagesToPdfWorkspace = lazy(async () => ({ default: (await import('./tools/pdf/workspaces')).ImagesToPdfWorkspace }))
const QrWorkspace = lazy(async () => ({ default: (await import('./tools/qr/workspace')).QrWorkspace }))
const ToolInProgress = lazy(async () => ({ default: (await import('./tools/shell')).ToolInProgress }))

const navItems = [
  { label: 'Home', icon: Home, href: '/', end: true },
  { label: 'Browse', icon: Menu, href: '/browse' },
  { label: 'Pinned', icon: Heart, href: '/pinned' },
  { label: 'History', icon: Clock3, href: '/history' },
]

function useLocalGreeting() {
  const [greeting, setGreeting] = useState(() => localGreeting(new Date().getHours()))
  useEffect(() => {
    const updateGreeting = () => setGreeting(localGreeting(new Date().getHours()))
    const timer = window.setInterval(updateGreeting, 60_000)
    return () => window.clearInterval(timer)
  }, [])
  return greeting
}

function ToolIcon({ tool, size = 23 }: { tool: ToolDefinition; size?: number }) {
  const Icon = tool.icon
  return <Icon size={size} strokeWidth={2} aria-hidden="true" />
}

function ToolCard({ tool, compact = false, showPinControl = true }: { tool: ToolDefinition; compact?: boolean; showPinControl?: boolean }) {
  const navigate = useNavigate()
  const { pinnedToolIds, togglePin } = useDrawerStore()
  const isPinned = pinnedToolIds.includes(tool.id)

  const openTool = () => navigate(`/tool/${tool.id}`)
  return (
    <article className={compact ? 'tool-card tool-card-compact' : 'tool-card'}>
      <button className="tool-card-main" onClick={openTool} type="button">
        <span className="tool-icon"><ToolIcon tool={tool} /></span>
        <span className="tool-copy">
          <strong>{tool.name}</strong>
          <small>{tool.description}</small>
        </span>
        {compact ? <ChevronRight size={18} aria-hidden="true" /> : null}
      </button>
      {showPinControl ? <button
        className={isPinned ? 'pin-button is-pinned' : 'pin-button'}
        type="button"
        aria-pressed={isPinned}
        aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${tool.name}`}
        onClick={() => togglePin(tool.id)}
      >
        <Pin size={16} fill={isPinned ? 'currentColor' : 'none'} aria-hidden="true" />
      </button> : null}
    </article>
  )
}

function Sidebar() {
  const displayName = useDrawerStore((state) => state.displayName)
  const greeting = useLocalGreeting()
  return (
    <aside className="sidebar" aria-label="Primary navigation">
      <Link className="brand" to="/" aria-label="DailyDrawer home">
        <BrandMark compact />
        <span><strong>Daily</strong>Drawer</span>
      </Link>
      <p className="brand-tagline">Simple tools for everyday tasks.</p>
      <nav className="side-nav">
        {navItems.map(({ label, icon: Icon, href, end }) => (
          <NavLink key={label} className={({ isActive }) => isActive ? 'nav-link is-active' : 'nav-link'} to={href} end={end}>
            <Icon size={20} aria-hidden="true" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-foot">
        <Link className="nav-link" to="/settings"><Settings size={20} aria-hidden="true" /> Settings</Link>
        {displayName ? <p><span className="avatar" aria-hidden="true">{displayName.slice(0, 1).toUpperCase()}</span><span><small>{greeting},</small><strong>{displayName}</strong></span></p> : null}
      </div>
    </aside>
  )
}

function SearchSurface({ query, onQueryChange, category, onCategoryChange }: {
  query: string
  onQueryChange: (value: string) => void
  category: CategoryFilter
  onCategoryChange: (value: CategoryFilter) => void
}) {
  const [open, setOpen] = useState(false)
  const filtered = searchTools(query, category)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <>
      <div className="search-wrap">
        <Search size={22} aria-hidden="true" />
        <label className="sr-only" htmlFor="tool-search">Search DailyDrawer tools</label>
        <input
          id="tool-search"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search tools…"
          autoComplete="off"
        />
        <button type="button" className="shortcut" onClick={() => setOpen(true)} aria-label="Open tool search">
          <Command size={14} aria-hidden="true" /> K
        </button>
      </div>
      <div className="filter-row" role="group" aria-label="Filter tools by category">
        {categories.map((item) => (
          <button key={item} type="button" className={category === item ? 'filter is-selected' : 'filter'} onClick={() => onCategoryChange(item)}>
            {item}
          </button>
        ))}
      </div>
      {open && query ? (
        <section className="search-results" aria-live="polite" aria-label="Search results">
          <div className="search-results-heading"><span>{filtered.length} matching {filtered.length === 1 ? 'tool' : 'tools'}</span><button type="button" onClick={() => setOpen(false)} aria-label="Close search results"><X size={17} /></button></div>
          {filtered.length ? filtered.slice(0, 5).map((tool) => <ToolCard key={tool.id} tool={tool} compact />) : <p className="search-empty">No exact match yet. Try “PDF”, “image”, “loan”, or “JSON”.</p>}
        </section>
      ) : null}
    </>
  )
}

function SectionHeading({ title, id, action, onAction }: { title: string; id?: string; action?: string; onAction?: () => void }) {
  return <div className="section-heading"><h2 id={id}>{title}</h2>{action && onAction ? <button type="button" onClick={onAction}>{action}</button> : null}</div>
}

type DashboardView = 'home' | 'browse' | 'pinned' | 'history'

function Dashboard({ view = 'home' }: { view?: DashboardView }) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<CategoryFilter>('All')
  const [isManagingPinned, setIsManagingPinned] = useState(false)
  const navigate = useNavigate()
  const { pinnedToolIds, recentToolIds, clearRecents } = useDrawerStore()
  const displayName = useDrawerStore((state) => state.displayName)
  const greeting = useLocalGreeting()
  const pinned = useMemo(() => pinnedToolIds.map(findTool).filter((tool): tool is ToolDefinition => Boolean(tool)), [pinnedToolIds])
  const recents = useMemo(() => recentToolIds.map(findTool).filter((tool): tool is ToolDefinition => Boolean(tool)), [recentToolIds])
  const grouped = useMemo(() => ['Image', 'PDF', 'Calculate', 'Text'].map((name) => ({ name, count: tools.filter((tool) => tool.category === name).length })), [])

  return (
    <main className="dashboard" id="main-content">
      <header className="mobile-header"><Link className="brand" to="/"><BrandMark compact /><span><strong>Daily</strong>Drawer</span></Link><button className="icon-button" type="button" aria-label="Open settings" onClick={() => navigate('/settings')}><Settings /></button></header>
      <div className="welcome-row">
        <div><p className="eyebrow">UTILITY DRAWER</p><h1>{displayName ? `${greeting}, ${displayName}` : greeting}</h1><p>What would you like to do today?</p></div>
      </div>
      <SearchSurface query={query} onQueryChange={setQuery} category={category} onCategoryChange={setCategory} />

      {query || category !== 'All' ? <section className="dashboard-section search-panel"><SectionHeading title={query ? `Tools matching “${query}”` : `${category} tools`} /><div className="tool-grid">{searchTools(query, category).map((tool) => <ToolCard key={tool.id} tool={tool} />)}</div></section>
        : view === 'browse' ? <section className="dashboard-section"><SectionHeading title="All tools" id="all-tools-heading" /><div className="tool-grid">{tools.map((tool) => <ToolCard key={tool.id} tool={tool} />)}</div></section>
          : view === 'pinned' ? <section className="dashboard-section"><SectionHeading title="Pinned tools" id="pinned-heading" action={isManagingPinned ? 'Done' : 'Manage'} onAction={() => setIsManagingPinned((current) => !current)} />{isManagingPinned ? <p className="management-hint" role="status">Use each pin control to remove it from your drawer.</p> : null}{pinned.length ? <div className="tool-grid">{pinned.map((tool) => <ToolCard key={tool.id} tool={tool} showPinControl={isManagingPinned} />)}</div> : <p className="empty-state">Pin tools from Browse and they will stay here.</p>}</section>
            : view === 'history' ? <section className="dashboard-section"><SectionHeading title="Recent tools" id="recent-heading" action={recents.length ? 'Clear' : undefined} onAction={clearRecents} />{recents.length ? <div className="recent-list">{recents.map((tool) => <ToolCard key={tool.id} tool={tool} compact />)}</div> : <p className="empty-state">Tools you open will appear here for quick return visits.</p>}</section>
              : <>
        <section className="dashboard-section" aria-labelledby="pinned-heading">
          <SectionHeading title="Pinned tools" id="pinned-heading" action={isManagingPinned ? 'Done' : 'Manage'} onAction={() => setIsManagingPinned((current) => !current)} />
          {isManagingPinned ? <p className="management-hint" role="status">Use each pin control to remove it from your drawer.</p> : null}
          {pinned.length ? <div className="tool-grid">{pinned.map((tool) => <ToolCard key={tool.id} tool={tool} showPinControl={isManagingPinned} />)}</div> : <p className="empty-state">Pin the tools you reach for most and they will stay here.</p>}
        </section>
        <section className="dashboard-section" aria-labelledby="recent-heading">
          <SectionHeading title="Recent tools" id="recent-heading" action={recents.length ? 'Clear' : undefined} onAction={clearRecents} />
          {recents.length ? <div className="recent-list">{recents.map((tool) => <ToolCard key={tool.id} tool={tool} compact />)} </div> : <p className="empty-state">Tools you open will appear here for quick return visits.</p>}
        </section>
        <section className="dashboard-section" aria-labelledby="browse-heading">
          <SectionHeading title="Browse by category" id="browse-heading" action="View all" onAction={() => navigate('/browse')} />
          <div className="category-grid">{grouped.map(({ name, count }) => {
            const Icon = name === 'Image' ? ImageIcon : name === 'PDF' ? FileStack : name === 'Calculate' ? Calculator : Type
            return <button className="category-card" key={name} type="button" onClick={() => setCategory(name as CategoryFilter)}><span className="category-symbol" aria-hidden="true"><Icon size={18} /></span><span><strong>{name} tools</strong><small>{count} tools</small></span><ChevronRight size={18} aria-hidden="true" /></button>
          })}</div>
        </section>
      </>}
    </main>
  )
}

function SettingsPage() {
  const navigate = useNavigate()
  const displayName = useDrawerStore((state) => state.displayName)
  const setDisplayName = useDrawerStore((state) => state.setDisplayName)
  const [name, setName] = useState(displayName ?? '')
  const [status, setStatus] = useState<string | null>(null)
  const save = (event: React.FormEvent) => { event.preventDefault(); setDisplayName(name); setStatus(name.trim() ? 'Name saved on this device.' : 'Name removed from this device.') }
  return <main className="settings-page" id="main-content"><button type="button" className="back-link" onClick={() => navigate(-1)}>← Back to drawer</button><div className="tool-workspace-heading"><p className="eyebrow">SETTINGS</p><h1>Personalize DailyDrawer</h1><p>Your preferences stay in this browser.</p></div><form className="settings-panel" onSubmit={save}><label htmlFor="display-name">Your name <span>optional</span><input id="display-name" value={name} maxLength={40} autoComplete="name" onChange={(event) => { setName(event.target.value); setStatus(null) }} /></label><p className="form-note">We use it only in your greeting and sidebar. Leave it empty to show no name.</p><button className="primary-action" type="submit">Save name</button>{status ? <p className="copy-status" role="status">{status}</p> : null}</form></main>
}

function ToolPlaceholder() {
  const { toolId } = useParams()
  const navigate = useNavigate()
  const { openTool } = useDrawerStore()
  const tool = toolId ? findTool(toolId) : undefined
  useEffect(() => { if (tool) openTool(tool.id) }, [openTool, tool])
  if (!tool) return <Navigate to="/" replace />
  const onBack = () => navigate(-1)
  const workspace = tool.id === 'sip-calculator' ? <SipWorkspace onBack={onBack} />
    : tool.id === 'cagr-calculator' ? <CagrWorkspace onBack={onBack} />
      : tool.id === 'emi-calculator' ? <EmiWorkspace onBack={onBack} />
        : tool.id === 'json-format' ? <JsonWorkspace onBack={onBack} />
          : tool.id === 'image-compress' ? <ImageCompressWorkspace onBack={onBack} />
            : tool.id === 'image-resize' ? <ImageResizeWorkspace onBack={onBack} />
              : tool.id === 'image-convert' ? <ImageConvertWorkspace onBack={onBack} />
                : tool.id === 'image-remove-metadata' ? <ImageMetadataWorkspace onBack={onBack} />
                  : tool.id === 'pdf-merge' ? <PdfMergeWorkspace onBack={onBack} />
                    : tool.id === 'pdf-split' ? <PdfSplitWorkspace onBack={onBack} />
                      : tool.id === 'images-to-pdf' ? <ImagesToPdfWorkspace onBack={onBack} />
                        : tool.id === 'qr-generate' ? <QrWorkspace onBack={onBack} />
                          : <ToolInProgress title={tool.name} category={tool.category} description={tool.description} onBack={onBack} />
  return <Suspense fallback={<main className="tool-workspace" id="main-content"><p className="tool-loading">Loading your local tool…</p></main>}>{workspace}</Suspense>
}

function BottomNav() {
  return <nav className="bottom-nav" aria-label="Mobile navigation">{navItems.map(({ label, icon: Icon, href, end }) => <NavLink key={label} to={href} end={end} className={({ isActive }) => isActive ? 'is-active' : ''}><Icon size={20} aria-hidden="true" /><span>{label}</span></NavLink>)}</nav>
}

export function App() {
  const location = useLocation()
  const isFocus = location.pathname.startsWith('/tool/') || location.pathname === '/settings'
  return <div className={isFocus ? 'app-shell is-focus' : 'app-shell'}><a className="skip-link" href="#main-content">Skip to content</a><Sidebar /><Routes><Route path="/" element={<Dashboard />} /><Route path="/browse" element={<Dashboard view="browse" />} /><Route path="/pinned" element={<Dashboard view="pinned" />} /><Route path="/history" element={<Dashboard view="history" />} /><Route path="/settings" element={<SettingsPage />} /><Route path="/tool/:toolId" element={<ToolPlaceholder />} /><Route path="*" element={<Navigate to="/" replace />} /></Routes><BottomNav /></div>
}
